import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import FigureQcm from "./FigureQcm";
import Maths from "./Maths";
import {
  api,
  type Anomalie,
  type Capacite,
  type PropositionCapacites,
  type QuestionBanque,
} from "./api";

const NIVEAUX: Record<string, string> = {
  seconde: "Seconde",
  "1spe": "1re spécialité",
  tspe: "Terminale spécialité",
};

export default function Banque() {
  const [questions, setQuestions] = useState<QuestionBanque[] | null>(null);
  const [niveau, setNiveau] = useState("seconde");
  const [filtre, setFiltre] = useState("");
  const [niveauFiltre, setNiveauFiltre] = useState("tous");
  const [anomalies, setAnomalies] = useState<Anomalie[] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [encours, setEncours] = useState(false);
  const [ouverte, setOuverte] = useState<number | null>(null);
  // La proposition de rattachement, relue avant d'être écrite. Elle ne vit
  // que dans l'écran : quitter la page la perd, et c'est assumé — ce qu'on
  // n'écrit jamais deux fois mérite d'être regardé une fois.
  const [proposition, setProposition] = useState<PropositionCapacites | null>(null);
  const [referentiel, setReferentiel] = useState<Capacite[]>([]);
  const [appel, setAppel] = useState(false);
  const [montrerRetirees, setMontrerRetirees] = useState(false);
  const fichier = useRef<HTMLInputElement>(null);

  const recharger = useCallback(() => {
    api.lireBanque().then(setQuestions).catch((e) => setErreur((e as Error).message));
  }, []);

  useEffect(recharger, [recharger]);

  const verser = async (choisi: File) => {
    setEncours(true);
    setErreur(null);
    setAnomalies(null);
    try {
      const bilan = await api.verserEnBanque(niveau, choisi);
      setAnomalies(bilan.anomalies);
      setErreur(null);
      recharger();
    } catch (e) {
      setErreur((e as Error).message);
    } finally {
      setEncours(false);
      if (fichier.current) fichier.current.value = "";
    }
  };

  // Le niveau filtre, il n'exclut pas : composer un QCM de 1re spé en
  // piochant deux automatismes de seconde est un geste courant du métier.
  const visibles = useMemo(() => {
    const cherche = filtre.trim().toLowerCase();
    return (questions ?? []).filter(
      (q) =>
        (montrerRetirees || q.retiree_le === null) &&
        (niveauFiltre === "tous" || q.niveau === niveauFiltre) &&
        (cherche === "" ||
          q.identifiant.toLowerCase().includes(cherche) ||
          q.enonce.toLowerCase().includes(cherche)),
    );
  }, [questions, filtre, niveauFiltre, montrerRetirees]);

  const proposer = async (pourNiveau: string) => {
    setAppel(true);
    setErreur(null);
    setAnomalies(null);
    try {
      const [propose, codes] = await Promise.all([
        api.proposerCapacitesBanque(pourNiveau),
        api.capacitesDuNiveau(pourNiveau),
      ]);
      setProposition(propose);
      setReferentiel(codes);
    } catch (e) {
      setErreur((e as Error).message);
    } finally {
      setAppel(false);
    }
  };

  const modifierCapacites = (ref: string, codes: string[]) =>
    setProposition((precedent) =>
      precedent === null
        ? precedent
        : {
            ...precedent,
            propose: {
              capacites_par_question: precedent.propose.capacites_par_question.map(
                (c) => (c.ref === ref ? { ...c, capacites: codes } : c),
              ),
            },
          },
    );

  const enregistrerCapacites = async () => {
    if (!proposition) return;
    try {
      const bilan = await api.enregistrerCapacitesBanque(
        proposition.niveau,
        proposition.propose,
      );
      setProposition(null);
      setErreur(null);
      setAnomalies(
        bilan.avertissements.map((message) => ({
          gravite: "avertissement" as const,
          message,
        })),
      );
      recharger();
    } catch (e) {
      setErreur((e as Error).message);
    }
  };

  const retirer = async (identifiant: string, retiree: boolean) => {
    try {
      await api.retirerQuestion(identifiant, retiree);
      recharger();
    } catch (e) {
      setErreur((e as Error).message);
    }
  };

  const supprimer = async (identifiant: string) => {
    try {
      await api.supprimerQuestion(identifiant);
      recharger();
    } catch (e) {
      setErreur((e as Error).message);
    }
  };

  if (!questions) return <p>Chargement…</p>;

  const niveaux = [...new Set(questions.map((q) => q.niveau))];
  const sansCapacite = niveaux
    .map((code) => ({
      code,
      nombre: questions.filter(
        (q) => q.niveau === code && q.capacites.length === 0,
      ).length,
    }))
    .filter((n) => n.nombre > 0);
  const parCode = new Map(referentiel.map((c) => [c.code, c]));
  const retirees = questions.filter((q) => q.retiree_le !== null).length;
  // Le décompte doit parler de ce que la table montre. Compter les retirées
  // avec les autres annonçait « 26 questions » au-dessus d'une liste vide —
  // le compteur avait l'air figé alors qu'il comptait autre chose.
  const enCirculation = questions.length - retirees;
  const jamaisServies = questions.filter(
    (q) => q.retiree_le === null && q.qcm_servis === 0,
  ).length;
  const filtree = filtre.trim() !== "" || niveauFiltre !== "tous";

  return (
    <section className="banque">
      <h2>Banque de questions</h2>
      <p className="aide">
        Les questions vivent ici, indépendamment des QCM qui les servent :
        elles traversent les classes et les années. Verser un fichier ne crée
        aucun QCM — c'est ensuite, en composant, qu'on y pioche. Le niveau dit
        d'où vient une question, pas qui peut s'en servir : un QCM de 1re spé
        reprend légitimement un automatisme de seconde, et c'est ainsi que le
        suivi par capacité garde son sens d'une année sur l'autre.
      </p>

      <div className="actions">
        <label>
          Niveau{" "}
          <select value={niveau} onChange={(e) => setNiveau(e.target.value)}>
            {Object.entries(NIVEAUX).map(([code, libelle]) => (
              <option key={code} value={code}>
                {libelle}
              </option>
            ))}
          </select>
        </label>
        <input
          ref={fichier}
          type="file"
          accept=".typ"
          disabled={encours}
          onChange={(e) => {
            const choisi = e.target.files?.[0];
            if (choisi) verser(choisi);
          }}
        />
        {encours && <span className="message">Lecture…</span>}
      </div>

      {erreur && <p className="erreur">{erreur}</p>}
      {anomalies !== null && (
        <p className="message">
          Questions versées.
          {anomalies.length > 0 && ` ${anomalies.length} point(s) d'attention.`}
        </p>
      )}
      {anomalies !== null && anomalies.length > 0 && (
        <div className="anomalies avertissements">
          <ul>
            {anomalies.map((a) => (
              <li key={a.message}>{a.message}</li>
            ))}
          </ul>
        </div>
      )}

      {sansCapacite.length > 0 && proposition === null && (
        <div className="anomalies avertissements">
          <strong>Questions sans rattachement au programme</strong>
          <p className="aide">
            Tant qu'une question n'a pas de capacité, elle n'existe sur aucun
            axe de comparaison — et la piocher dans un QCM d'un autre niveau
            l'étiquetterait au mauvais endroit. Le rattachement s'attribue une
            seule fois et ne se rejoue jamais : c'est pourquoi il se relit
            avant d'être écrit.
          </p>
          <div className="actions">
            {sansCapacite.map(({ code, nombre }) => (
              <button
                key={code}
                type="button"
                onClick={() => proposer(code)}
                disabled={appel}
              >
                {appel
                  ? "Appel en cours…"
                  : `Proposer les capacités — ${nombre} question(s) de ${
                      NIVEAUX[code] ?? code
                    } (payant)`}
              </button>
            ))}
          </div>
        </div>
      )}

      {proposition !== null && (
        <div className="relecture">
          <header className="saisie-entete">
            <div>
              <h3>Rattachement au programme — {NIVEAUX[proposition.niveau]}</h3>
              <p className="sous-titre">
                {proposition.propose.capacites_par_question.length} questions
                {proposition.cout_dollars !== null &&
                  ` · appel facturé ${proposition.cout_dollars.toFixed(3)} $`}
                {" · rien n'est écrit tant que vous n'avez pas enregistré"}
              </p>
            </div>
            <div className="actions">
              <button type="button" onClick={enregistrerCapacites}>
                Enregistrer les rattachements
              </button>
              <button type="button" onClick={() => setProposition(null)}>
                Abandonner
              </button>
            </div>
          </header>

          {proposition.anomalies.length > 0 && (
            <ul>
              {proposition.anomalies.map((a) => (
                <li key={a.message}>{a.message}</li>
              ))}
            </ul>
          )}

          <table className="questions-qcm">
            <tbody>
              {proposition.propose.capacites_par_question.map((ligne) => {
                const question = proposition.questions.find(
                  (q) => q.ref === ligne.ref,
                );
                return (
                  <tr key={ligne.ref}>
                    <td>
                      <div className="enonce-qcm">
                        <Maths
                          texte={question?.enonce ?? ligne.ref}
                          syntaxe="typst"
                        />
                      </div>
                      <FigureQcm
                        identifiant={ligne.ref}
                        presente={question?.a_une_figure}
                      />
                      <div className="fichier">{ligne.ref}</div>
                    </td>
                    <td>
                      <div className="capacites">
                        {ligne.capacites.length === 0 && (
                          <span className="attente">Aucune capacité</span>
                        )}
                        {ligne.capacites.map((code) => (
                          <span
                            key={code}
                            className="puce-capacite"
                            title={parCode.get(code)?.libelle ?? code}
                          >
                            {code}
                            <button
                              type="button"
                              onClick={() =>
                                modifierCapacites(
                                  ligne.ref,
                                  ligne.capacites.filter((x) => x !== code),
                                )
                              }
                            >
                              ×
                            </button>
                          </span>
                        ))}
                        <select
                          value=""
                          aria-label={`Ajouter une capacité à ${ligne.ref}`}
                          onChange={(e) => {
                            const code = e.target.value;
                            if (!code || ligne.capacites.includes(code)) return;
                            modifierCapacites(ligne.ref, [...ligne.capacites, code]);
                          }}
                        >
                          <option value="">+ capacité…</option>
                          {referentiel.map((capacite) => (
                            <option key={capacite.code} value={capacite.code}>
                              {capacite.code} — {capacite.libelle.slice(0, 70)}
                            </option>
                          ))}
                        </select>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <header className="saisie-entete">
        <p className="sous-titre">
          {enCirculation} en circulation
          {jamaisServies > 0 && ` · ${jamaisServies} jamais servie(s)`}
          {retirees > 0 && ` · ${retirees} retirée(s)`}
          {filtree && ` · ${visibles.length} affichée(s)`}
        </p>
        <div className="actions">
          <input
            type="search"
            placeholder="Chercher un identifiant, un énoncé…"
            value={filtre}
            onChange={(e) => setFiltre(e.target.value)}
          />
          <select
            value={niveauFiltre}
            onChange={(e) => setNiveauFiltre(e.target.value)}
          >
            <option value="tous">Tous les niveaux</option>
            {niveaux.map((code) => (
              <option key={code} value={code}>
                {NIVEAUX[code] ?? code}
              </option>
            ))}
          </select>
          {retirees > 0 && (
            <label>
              <input
                type="checkbox"
                checked={montrerRetirees}
                onChange={(e) => setMontrerRetirees(e.target.checked)}
              />{" "}
              voir les {retirees} retirée(s)
            </label>
          )}
        </div>
      </header>

      {visibles.length === 0 && (
        <p className="aide">
          {questions.length === 0
            ? "La banque est vide. Versez un fichier de questions ci-dessus."
            : "Aucune question ne correspond."}
        </p>
      )}

      <table className="questions-qcm">
        <tbody>
          {visibles.map((question) => (
            <tr key={question.id} className={question.retiree_le ? "retiree" : ""}>
              <th>{NIVEAUX[question.niveau] ?? question.niveau}</th>
              <td>
                <button
                  type="button"
                  className="enonce-banque"
                  onClick={() =>
                    setOuverte(ouverte === question.id ? null : question.id)
                  }
                >
                  <div className="enonce-qcm">
                    <Maths texte={question.enonce} syntaxe="typst" />
                  </div>
                  <FigureQcm
                    identifiant={question.identifiant}
                    presente={question.a_une_figure}
                  />
                </button>
                <div className="fichier">
                  {question.identifiant}
                  {question.capacites.length > 0 &&
                    ` · ${question.capacites.join(", ")}`}
                </div>

                {ouverte === question.id && (
                  <ul className="reponses-banque">
                    {question.reponses.map((reponse, rang) => (
                      <li key={rang} className={reponse.exacte ? "exacte" : ""}>
                        <Maths texte={reponse.texte} syntaxe="typst" />
                        {!reponse.exacte && (
                          <span className="diagnostic">
                            {reponse.erreur ?? "erreur non décrite"}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </td>
              <td className="nombre">
                {question.qcm_servis === 0 ? (
                  <span className="attente">jamais servie</span>
                ) : (
                  <>
                    {question.qcm_servis} QCM
                    {question.servie && <div className="fichier">corrigée</div>}
                  </>
                )}
                <div className="gestes">
                  {question.retiree_le !== null ? (
                    <button
                      type="button"
                      className="puce"
                      onClick={() => retirer(question.identifiant, false)}
                    >
                      remettre
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="puce"
                      title="Ne plus la proposer à la composition. N'efface rien."
                      onClick={() => retirer(question.identifiant, true)}
                    >
                      retirer
                    </button>
                  )}
                  {/* Supprimer n'est offert que là où c'est possible : dès
                      qu'un QCM la porte, la clé étrangère le refuse — et
                      c'est le retrait qui prend le relais. */}
                  {question.qcm_servis === 0 && (
                    <button
                      type="button"
                      className="puce"
                      title="Elle n'a jamais figuré dans un QCM"
                      onClick={() => supprimer(question.identifiant)}
                    >
                      supprimer
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
